import { useTranslation } from 'react-i18next';

import { NotificationItem } from '@/redux/models';
import {
  resolveNotificationLink,
  selectCampaignCopy,
} from '@/api/amlbotAPI/notifications';
import { resolveNotificationLinkKey } from '@/config/notificationLinks';

// Shared by the row and the notification popup, so the two can never show a
// notification differently.

// i18n.language is 'en' | 'ua' | 'ru' (see src/i18n/index.ts), but dayjs's
// Ukrainian locale code is 'uk', not 'ua'.
export const DAYJS_LOCALE: Record<string, string> = {
  ua: 'uk',
  ru: 'ru',
  en: 'en',
};

// Maps a notification type to a sprite icon. Every name here is verified to exist
// in src/assets/icons — SvgIcon renders nothing for an unknown name.
export const ICON_BY_TYPE: Record<string, string> = {
  registration: 'profile',
  two_fa_enabled: 'lock',
  two_fa_disabled: 'lock',
  balance_refilled: 'billing',
  api_key_issued: 'key',
  referral_opened: 'users',
  tx_monitoring_opened: 'compliance-dashboard',
  address_monitoring_opened: 'monitoring',
  risk_score_alert: 'risk',
  promo: 'percent',
};

export const useNotificationCopy = (
  item: NotificationItem,
): { title: string; body: string } => {
  const { t, i18n } = useTranslation();

  if (item.kind === 'campaign' && item.campaign) {
    return selectCampaignCopy(item.campaign, i18n.language);
  }

  return {
    title: item.payload?.title
      ? String(item.payload.title)
      : t(`translation.notifications.${item.type}.title`, {
          ...(item.payload || {}),
        }),
    body: item.payload?.body
      ? String(item.payload.body)
      : t(`translation.notifications.${item.type}.body`, {
          ...(item.payload || {}),
        }),
  };
};

export interface NotificationDestination {
  href: string;
  label: string;
  external: boolean;
}

/**
 * Where the notification as a whole leads, if anywhere — the popup's button.
 *
 * A personal notification's `link` is a destination KEY. An unknown key, or a legacy
 * row still holding a literal path, resolves to null and offers no destination. A
 * campaign carries its links inside the message; only a campaign sent before that
 * change has a separate call to action (ctaLink + ctaLabel).
 */
export const useNotificationDestination = (
  item: NotificationItem,
): NotificationDestination | null => {
  const { t } = useTranslation();

  if (item.kind === 'personal') {
    const path = resolveNotificationLinkKey(item.link);
    return path
      ? {
          href: path,
          label: t(`translation.notifications.links.${item.link}`),
          external: false,
        }
      : null;
  }

  const cta = item.campaign;
  if (!cta?.ctaLink || !cta.ctaLabel) return null;

  const resolved = resolveNotificationLink(cta.ctaLink);
  if (resolved.kind === 'rejected') return null;

  return {
    href: resolved.href,
    label: cta.ctaLabel,
    external: resolved.kind === 'external',
  };
};
